import {
  useFetcher,
  useLoaderData,
  useParams,
} from "@remix-run/react";
import clsx from "clsx";
import {
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
} from "react";
import type { loader } from "./route";
import {
  buttonBase,
  inputBase,
} from "~/components/Common/FormStyle";
import { usePaginteData } from "~/utils/hook/usePaginteData";
import {
  getFormProps,
  getTextareaProps,
  useForm,
} from "@conform-to/react";
import type { commentTableWithUserScheme } from "~/database/post.model";
import { commentTable } from "~/database/post.model";
import type { action } from "../posts.$postId.add-comment/route";
import { object } from "valibot";
import { parseWithValibot as conformParse } from "conform-to-valibot";
import toast from "react-hot-toast";
import {
  useAuthUser,
  useIsLogin,
} from "~/utils/hook/useIsLogin";
import { FloatMenu } from "~/components/Common/FloatMenu";
import {
  numToArray,
  replaceToBr,
} from "~/utils/common";
import { convertName } from "~/utils/convertName";
import { dateDisplay } from "~/utils/dateDisplay";
import ConfirmModal from "~/components/Editor/ConfirmModal";

interface CommentListProps {}

export const CommentFormSchema = object({
  content: commentTable.entries.content,
});

export const CommentList: FunctionComponent<
  CommentListProps
> = () => {
  const param = useParams();
  const { commentCursor: _commentCursor } =
    useLoaderData<typeof loader>();

  const { nextLoad, cursor: userPosts } =
    usePaginteData(
      _commentCursor,
      `/posts/${param.postId}/comments-load`,
    );

  const [form, { content }] = useForm({
    id: "Comment",
    onValidate({ formData }) {
      return conformParse(formData, {
        schema: CommentFormSchema,
      });
    },
    defaultValue: {
      content: "",
    },
    onSubmit(e) {
      console.log("content:", content);
    },
  });

  let fetcher = useFetcher<typeof action>();

  useEffect(() => {
    if (!fetcher.data) return;
    if (fetcher.data?.success) {
      form.reset();
      toast.success("コメントしました！", {
        duration: 4000,
      });
    } else {
      toast.error("エラーが発生しました。", {
        duration: 4000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.data?.success]);
  const { isLogin } = useIsLogin();
  return (
    <div
      className={clsx(
        "mx-auto w-1305",
        "max-md:w-360",
      )}
    >
      <h2
        className={clsx(
          "text-24",
          "font-bold",
          "mt-60",
          "max-md:mt-40",
        )}
      >
        コメント
      </h2>
      {isLogin && (
        <fetcher.Form
          method="post"
          {...getFormProps(form)}
          action={`/posts/${param.postId}/add-comment`}
        >
          <textarea
            {...getTextareaProps(content)}
            className={clsx(
              inputBase(),
              "mt-20",
              "resize-none",
              "w-full",
            )}
            placeholder="コメントを追加"
            rows={4}
          ></textarea>
          <div
            className={clsx(
              "mt-20",
              "max-md:text-center",
            )}
          >
            <button
              type="submit"
              className={buttonBase({
                size: "sm",
                variant: "default",
              })}
            >
              追加
            </button>
          </div>
        </fetcher.Form>
      )}
      {
        // ログインしていない場合
        !isLogin && (
          <div className={clsx()}>
            <div
              className={clsx(
                "text-center",
                "mt-20",
                "text-gray-400",
                "text-14",
              )}
            >
              <a
                href="/login"
                className={clsx("underline")}
              >
                ログイン
              </a>
              すると
              <br />
              コメントができます
            </div>
          </div>
        )
      }
      <div
        className={clsx("mt-30", "max-md:mt-40")}
      >
        <ul
          className={clsx(
            "gap-16",
            "mt-24",
            "w-1300",
            "mx-auto",
            "flex",
            "flex-col",
            "divide-y-2",

            "max-md:gap-30",
            "max-md:w-360",
            "max-md:mr-0",
          )}
        >
          {userPosts.data.length > 0 &&
            userPosts.data?.map((el, i) => {
              return (
                <CommentItem
                  el={el}
                  i={i}
                  key={i}
                />
              );
            })}
          {userPosts.data.length === 0 && (
            <div
              className={clsx(
                "text-center",
                "py-20",
                "text-gray-400",
              )}
            >
              コメントはありません
            </div>
          )}
        </ul>
      </div>
      {userPosts.hasNextPage && (
        <div
          className={clsx("text-center", "mt-60")}
        >
          <button
            className={clsx(
              "bg-red",
              buttonBase({
                size: "md",
                round: "full",
                variant: "default",
              }),
            )}
            onClick={() => {
              nextLoad();
            }}
          >
            もっと見る
          </button>
        </div>
      )}
    </div>
  );
};

interface CommentItemProps {
  el: commentTableWithUserScheme;
  i: number;
}

const CommentItem: FunctionComponent<
  CommentItemProps
> = ({ el, i }) => {
  const { user } = useAuthUser();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuClose = useCallback(() => {
    setMenuOpen(false);
  }, []);
  useEffect(() => {
    if (menuOpen) {
      window.addEventListener("click", menuClose);
      return () => {
        window.removeEventListener(
          "click",
          menuClose,
        );
      };
    }
  }, [menuClose, menuOpen]);
  const [deleteModalOpen, setDeleteModalOpen] =
    useState(false);

  const fetcher = useFetcher<typeof action>();
  const fetcherDel = useFetcher<typeof action>();
  const param = useParams();

  const [form, { content }] = useForm({
    id: "Comment-Update" + el.id,
    onValidate({ formData }) {
      return conformParse(formData, {
        schema: CommentFormSchema,
      });
    },
    defaultValue: {
      content: el.content,
    },
    onSubmit(e) {
      console.log("content:", content);
    },
  });

  useEffect(() => {
    if (!fetcher.data) return;
    if (fetcher.state !== "idle") return;
    if (fetcher.data?.success) {
      form.reset();
      setEditOpen(false);
      toast.success("コメントしました！", {
        duration: 4000,
      });
    } else {
      toast.error("エラーが発生しました。", {
        duration: 4000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.data?.success, fetcher.state]);

  useEffect(() => {
    if (!fetcherDel.data) return;
    if (fetcherDel.state !== "idle") return;
    if (fetcherDel.data?.success) {
      toast.success("コメントを削除しました。", {
        duration: 4000,
      });
    } else {
      toast.error("エラーが発生しました。", {
        duration: 4000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetcherDel.data?.success,
    fetcherDel.state,
  ]);

  const [editOpen, setEditOpen] = useState(false);
  return (
    <li
      key={i}
      className={clsx(
        "grid",
        "grid-cols-[1fr_auto]",
        "py-10",
      )}
    >
      {deleteModalOpen && (
        <ConfirmModal
          onOk={() => {
            fetcherDel.submit(
              {},
              {
                method: "post",
                action: `/posts/${param.postId}/comments/${el.id}/delete`,
              },
            );

            setDeleteModalOpen(false);
          }}
          closeModal={() => {
            setDeleteModalOpen(false);
          }}
          texts={{
            modalHead: "コメント削除",
            head: "本当に削除しますか？",
          }}
        ></ConfirmModal>
      )}
      <div className={clsx()}>
        {!editOpen && (
          <div className={clsx()}>
            <div
              className={clsx(
                "text-12",
                "text-gray-500",
                user?.id === el.user_id &&
                  "text-green-500",
                "flex",
                "gap-15",
              )}
            >
              {convertName(el.public_users.name)}
              <div
                className={clsx("text-gray-400")}
              >
                {dateDisplay(el.created_at)}
              </div>
            </div>
            <div
              className={clsx(
                "mt-4",
                "text-15",
                "max-md:text-14",
              )}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: replaceToBr(el.content),
                }}
              />
            </div>
          </div>
        )}
        {editOpen && (
          <fetcher.Form
            method="post"
            {...getFormProps(form)}
            action={`/posts/${param.postId}/comments/${el.id}/update`}
          >
            <textarea
              {...getTextareaProps(content)}
              className={clsx(
                inputBase(),
                "mt-20",
                "resize-none",
                "w-full",
              )}
              placeholder="コメントを追加"
              rows={4}
            ></textarea>
            <div
              className={clsx(
                "mt-20",
                "flex",
                "gap-20",
              )}
            >
              <button
                type="button"
                className={buttonBase({
                  size: "sm",
                  variant: "ol",
                })}
                onClick={() => {
                  form.reset();
                  setEditOpen(false);
                }}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className={buttonBase({
                  size: "sm",
                  variant: "default",
                })}
              >
                更新
              </button>
            </div>
          </fetcher.Form>
        )}
      </div>
      {user?.id === el.user_id && (
        <button
          className={clsx(
            "h-30",
            "w-30",
            "flex",
            "items-center",
            "justify-center",
            "hover:bg-gray-200",
            "rounded-full",
            "transition-all",
            "relative",
          )}
          onClick={(e) => {
            setMenuOpen(!menuOpen);
            e.stopPropagation();
          }}
        >
          <FloatMenu menuOpen={menuOpen}>
            <ul>
              <li>
                <button
                  onClick={() => {
                    setEditOpen(true);
                  }}
                  className={clsx(
                    "py-8",
                    "block",
                    "w-full",
                    "hover:bg-slate-200",
                  )}
                >
                  編集
                </button>
              </li>
              <li
                className={clsx(
                  "text-red-600",
                  "border-t",
                )}
              >
                <button
                  className={clsx(
                    "py-8",
                    "w-full",
                    "block",
                    "hover:bg-slate-200",
                  )}
                  onClick={(e) => {
                    setDeleteModalOpen(true);
                    e.stopPropagation();
                  }}
                >
                  削除
                </button>
              </li>
            </ul>
          </FloatMenu>
          <div className={clsx("flex", "gap-4")}>
            {numToArray(3).map((el, i) => {
              return (
                <span
                  key={i}
                  className={clsx(
                    "w-4",
                    "h-4",
                    "bg-gray-700",
                    "rounded-full",
                  )}
                ></span>
              );
            })}
          </div>
        </button>
      )}
    </li>
  );
};

export default CommentItem;
